import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  User, 
  CheckCircle, 
  ArrowLeft, 
  ArrowRight,
  Stethoscope,
  Heart,
  Activity,
  Users,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { 
  Card, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Textarea, 
  StepIndicator,
  Badge,
  LoadingSpinner
} from '../../components/ui';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, generateTimeSlots, isTimeSlotAvailable, generateId } from '../../utils';
import { Service, Doctor, BookingFormData, Appointment } from '../../types';
import toast from 'react-hot-toast';

interface FormErrors {
  [key: string]: string;
}

const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const { services, doctors, appointments: existingAppointments, addAppointment } = useData();

  // Booking form data
  const [bookingData, setBookingData] = useState<BookingFormData>({
    serviceId: searchParams.get('service') || '',
    doctorId: searchParams.get('doctor') || '',
    date: '',
    time: '',
    patientInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      notes: ''
    }
  });

  const steps = [
    { title: 'Service', description: 'Choose service' },
    { title: 'Doctor', description: 'Select doctor' },
    { title: 'Date & Time', description: 'Pick schedule' },
    { title: 'Information', description: 'Your details' },
    { title: 'Confirmation', description: 'Review & book' }
  ];

  // Get available doctors for selected service
  const availableDoctors = bookingData.serviceId 
    ? doctors.filter(doctor => doctor.isAvailable)
    : [];

  // Get selected service and doctor
  const selectedService = services.find(s => s.id === bookingData.serviceId);
  const selectedDoctor = doctors.find(d => d.id === bookingData.doctorId);

  // Generate time slots for selected doctor and date
  const availableTimeSlots = React.useMemo(() => {
    if (!selectedDoctor || !bookingData.date) return [];
    
    const dayOfWeek = new Date(bookingData.date).getDay();
    const doctorSchedule = selectedDoctor.schedule.find(s => s.dayOfWeek === dayOfWeek);
    
    if (!doctorSchedule || !doctorSchedule.isWorking) return [];
    
    const slots = generateTimeSlots(doctorSchedule.startTime, doctorSchedule.endTime);
    return slots.filter(time => 
      isTimeSlotAvailable(bookingData.date, time, bookingData.doctorId, existingAppointments)
    );
  }, [selectedDoctor, bookingData.date, bookingData.doctorId, existingAppointments]);

  // Validation functions
  const validateStep = (step: number): boolean => {
    const errors: FormErrors = {};
    
    switch (step) {
      case 1:
        if (!bookingData.serviceId) errors.service = 'Please select a service';
        break;
      case 2:
        if (!bookingData.doctorId) errors.doctor = 'Please select a doctor';
        break;
      case 3:
        if (!bookingData.date) errors.date = 'Please select a date';
        if (!bookingData.time) errors.time = 'Please select a time';
        break;
      case 4:
        if (!bookingData.patientInfo.firstName) errors.firstName = 'First name is required';
        if (!bookingData.patientInfo.lastName) errors.lastName = 'Last name is required';
        if (!bookingData.patientInfo.email) errors.email = 'Email is required';
        if (!bookingData.patientInfo.phone) errors.phone = 'Phone number is required';
        if (!bookingData.patientInfo.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
        
        // Email validation
        if (bookingData.patientInfo.email && !/\S+@\S+\.\S+/.test(bookingData.patientInfo.email)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create appointment object
      const newAppointment = {
        patientId: generateId(),
        doctorId: bookingData.doctorId,
        serviceId: bookingData.serviceId,
        date: bookingData.date,
        time: bookingData.time,
        duration: selectedService?.duration || 30,
        status: 'scheduled' as const,
        notes: bookingData.patientInfo.notes || undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add to real data
      addAppointment(newAppointment);
      
      // Move to success step
      setCurrentStep(5);
      toast.success('Appointment booked successfully!');
      
    } catch (error) {
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceIcon = (category: string) => {
    const iconMap: { [key: string]: any } = {
      'General Medicine': Heart,
      'Cardiology': Activity,
      'Orthopedics': Users,
      'Dermatology': Stethoscope,
    };
    return iconMap[category] || Stethoscope;
  };

  // Reset doctor selection when service changes
  useEffect(() => {
    if (bookingData.serviceId && bookingData.doctorId) {
      const doctorStillAvailable = availableDoctors.find(d => d.id === bookingData.doctorId);
      if (!doctorStillAvailable) {
        setBookingData(prev => ({ ...prev, doctorId: '' }));
      }
    }
  }, [bookingData.serviceId, bookingData.doctorId, availableDoctors]);

  // Reset time when date changes
  useEffect(() => {
    if (bookingData.date && bookingData.time) {
      const timeStillAvailable = availableTimeSlots.includes(bookingData.time);
      if (!timeStillAvailable) {
        setBookingData(prev => ({ ...prev, time: '' }));
      }
    }
  }, [bookingData.date, bookingData.time, availableTimeSlots]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Book Your Appointment
          </h1>
          <p className="text-xl text-gray-600">
            Schedule your visit in just a few simple steps
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <StepIndicator steps={steps} currentStep={currentStep} />
        </div>

        {/* Main Content */}
        <Card className="mb-8">
          {/* Step 1: Service Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Choose Your Service
                </h2>
                <p className="text-gray-600">
                  Select the medical service you need
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service) => {
                  const ServiceIcon = getServiceIcon(service.category);
                  const isSelected = bookingData.serviceId === service.id;
                  
                  return (
                    <div
                      key={service.id}
                      onClick={() => setBookingData(prev => ({ ...prev, serviceId: service.id }))}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-blue-500' : 'bg-gray-100'
                        }`}>
                          <ServiceIcon className={`w-6 h-6 ${
                            isSelected ? 'text-white' : 'text-gray-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {service.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            {service.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <Badge variant="primary" size="sm">
                              {service.category}
                            </Badge>
                            <span className="font-bold text-green-600">
                              {formatCurrency(service.price)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Duration: {service.duration} minutes
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {formErrors.service && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{formErrors.service}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Doctor Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Choose Your Doctor
                </h2>
                <p className="text-gray-600">
                  Select from our available specialists
                </p>
              </div>

              {selectedService && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Selected Service:</strong> {selectedService.name} - {formatCurrency(selectedService.price)}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {availableDoctors.map((doctor) => {
                  const isSelected = bookingData.doctorId === doctor.id;
                  
                  return (
                    <div
                      key={doctor.id}
                      onClick={() => setBookingData(prev => ({ ...prev, doctorId: doctor.id }))}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <img
                          src={doctor.photo}
                          alt={doctor.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {doctor.name}
                          </h3>
                          <p className="text-sm text-blue-600 mb-2">
                            {doctor.specialty}
                          </p>
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`w-3 h-3 ${
                                    i < Math.floor(doctor.rating) 
                                      ? 'text-yellow-400' 
                                      : 'text-gray-300'
                                  }`}
                                >
                                  ★
                                </div>
                              ))}
                            </div>
                            <span className="text-xs text-gray-600">
                              {doctor.rating} ({doctor.reviewCount} reviews)
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {doctor.qualifications.slice(0, 2).map((qual, index) => (
                              <Badge key={index} variant="info" size="sm">
                                {qual}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {formErrors.doctor && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{formErrors.doctor}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Date & Time Selection */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Select Date & Time
                </h2>
                <p className="text-gray-600">
                  Choose your preferred appointment slot
                </p>
              </div>

              {selectedDoctor && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Selected Doctor:</strong> {selectedDoctor.name} - {selectedDoctor.specialty}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <DatePicker
                    label="Appointment Date"
                    value={bookingData.date}
                    onChange={(date) => setBookingData(prev => ({ ...prev, date, time: '' }))}
                    error={formErrors.date}
                    helperText="Select your preferred date"
                  />
                </div>
                
                {bookingData.date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Times
                    </label>
                    {availableTimeSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                        {availableTimeSlots.map((time) => (
                          <button
                            key={time}
                            onClick={() => setBookingData(prev => ({ ...prev, time }))}
                            className={`p-2 text-sm border rounded-lg transition-colors ${
                              bookingData.time === time
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {formatTime(time)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 p-4 border rounded-lg">
                        No available slots for this date. Please select another date.
                      </p>
                    )}
                    {formErrors.time && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.time}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Patient Information */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Your Information
                </h2>
                <p className="text-gray-600">
                  Please provide your contact details
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  value={bookingData.patientInfo.firstName}
                  onChange={(e) => setBookingData(prev => ({
                    ...prev,
                    patientInfo: { ...prev.patientInfo, firstName: e.target.value }
                  }))}
                  error={formErrors.firstName}
                  icon={<User className="w-4 h-4" />}
                />
                
                <Input
                  label="Last Name"
                  value={bookingData.patientInfo.lastName}
                  onChange={(e) => setBookingData(prev => ({
                    ...prev,
                    patientInfo: { ...prev.patientInfo, lastName: e.target.value }
                  }))}
                  error={formErrors.lastName}
                  icon={<User className="w-4 h-4" />}
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  value={bookingData.patientInfo.email}
                  onChange={(e) => setBookingData(prev => ({
                    ...prev,
                    patientInfo: { ...prev.patientInfo, email: e.target.value }
                  }))}
                  error={formErrors.email}
                  icon={<Mail className="w-4 h-4" />}
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  value={bookingData.patientInfo.phone}
                  onChange={(e) => setBookingData(prev => ({
                    ...prev,
                    patientInfo: { ...prev.patientInfo, phone: e.target.value }
                  }))}
                  error={formErrors.phone}
                  icon={<Phone className="w-4 h-4" />}
                />
                
                <DatePicker
                  label="Date of Birth"
                  value={bookingData.patientInfo.dateOfBirth}
                  onChange={(date) => setBookingData(prev => ({
                    ...prev,
                    patientInfo: { ...prev.patientInfo, dateOfBirth: date }
                  }))}
                  error={formErrors.dateOfBirth}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <Textarea
                label="Additional Notes (Optional)"
                placeholder="Any specific concerns or requirements..."
                value={bookingData.patientInfo.notes}
                onChange={(e) => setBookingData(prev => ({
                  ...prev,
                  patientInfo: { ...prev.patientInfo, notes: e.target.value }
                }))}
                rows={3}
              />
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Appointment Confirmed!
                </h2>
                <p className="text-gray-600">
                  Your appointment has been successfully booked
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="font-semibold text-green-800 mb-4">Appointment Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Service:</span>
                    <span className="font-medium">{selectedService?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Doctor:</span>
                    <span className="font-medium">{selectedDoctor?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Date:</span>
                    <span className="font-medium">{new Date(bookingData.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Time:</span>
                    <span className="font-medium">{formatTime(bookingData.time)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Duration:</span>
                    <span className="font-medium">{selectedService?.duration} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Cost:</span>
                    <span className="font-bold text-lg">{formatCurrency(selectedService?.price || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">What's Next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• You'll receive a confirmation email shortly</li>
                  <li>• Please arrive 15 minutes before your appointment</li>
                  <li>• Bring a valid ID and insurance information</li>
                  <li>• You can reschedule up to 24 hours in advance</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  Return Home
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/services')}
                  className="flex-1"
                >
                  Book Another Appointment
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Navigation Buttons */}
        {currentStep < 5 && (
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={currentStep === 1 ? 'invisible' : ''}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            {currentStep < 4 ? (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" color="white" className="mr-2" />
                    Booking...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Booking
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;